import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import './RealtimeWarning.css';

/**
 * RealtimeWarning - Detects when Supabase Realtime (WebSocket) connections fail
 * and shows a non-intrusive warning to the user.
 *
 * Common causes: ad blockers, privacy extensions, strict browser tracking protection,
 * or corporate firewalls blocking WebSocket connections to *.supabase.co.
 */
const RealtimeWarning = () => {
  const { supabase, isAuthenticated } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const channelRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Only probe for authenticated users (Realtime features require auth)
    if (!isAuthenticated || !supabase) return;

    // Check if user already dismissed this session
    if (sessionStorage.getItem('realtime_warning_dismissed') === 'true') {
      setDismissed(true);
      return;
    }

    // Create a lightweight probe channel to test Realtime connectivity
    const probeChannel = supabase.channel('realtime-probe');

    // Set a timeout — if we don't get SUBSCRIBED within 10s, something is blocking it
    timeoutRef.current = setTimeout(() => {
      setShowWarning(true);
    }, 10000);

    probeChannel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        // Connection works — clear the timeout and remove the probe
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setShowWarning(false);
        supabase.removeChannel(probeChannel);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        // Connection failed
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setShowWarning(true);
        supabase.removeChannel(probeChannel);
      }
    });

    channelRef.current = probeChannel;

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isAuthenticated, supabase]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('realtime_warning_dismissed', 'true');
  };

  if (!showWarning || dismissed) return null;

  return (
    <div className="realtime-warning" role="alert">
      <div className="realtime-warning-content">
        <span className="realtime-warning-icon">ℹ️</span>
        <span className="realtime-warning-text">
          <strong>Some background features are limited</strong> — Real-time notifications and
          messages may not update automatically. If you use an ad blocker or privacy extension,
          try whitelisting this site. See our <a href="/faq">FAQ</a> for details.
        </span>
        <button
          className="realtime-warning-dismiss"
          onClick={handleDismiss}
          title="Dismiss"
          aria-label="Dismiss live updates warning"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

export default RealtimeWarning;
